import RegionLocation from '../components/RegionLocation';
import { formatRegionLocation } from '../utils/regionLocation';
import React, { useEffect, useRef, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  Skeleton,
  TextField,
  Button,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  keyframes,
  useTheme,
  alpha
} from '@mui/material';
import {
  Speed as SpeedIcon,
  NetworkCheck as NetworkIcon,
  Computer as ComputerIcon,
  DragIndicator as DragIndicatorIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import api, { BotListResponse } from '../services/api';
import { PermissionGate } from '../components/PermissionGate';
import { useNotification } from '../contexts/NotificationContext';
import { useTenant } from '../contexts/TenantContext';
import { useGrafanaMetrics } from '../hooks/useGrafanaMetrics';
import { useDashboardBots } from '../hooks/useDashboardBots';
import { useAdaptivePolling } from '../hooks/useAdaptivePolling';
import { BotsIpRegionTable } from '../components/metrics/BotsIpRegionTable';
import { TimeseriesCard } from '../components/metrics/TimeseriesCard';
import MonitoringPanel from '../components/monitors/MonitoringPanel';
import type { Scenario } from '../types/attacks';
import { isSocketOperationAttackClass } from '../utils/telemetryTimeseriesPanels';

import dayjs from 'dayjs';

const TIME_PRESETS = [
  { label: 'Last 5 minutes', value: 5 },
  { label: 'Last 10 minutes', value: 10 },
  { label: 'Last 30 minutes', value: 30 },
  { label: 'Custom', value: 'custom' },
];

const PANEL_ORDER_STORAGE_KEY = 'dashboard.telemetry.panelOrder.v1';
const PINNED_PANELS_STORAGE_KEY = 'dashboard.telemetry.pinnedPanels.v1';

// Animations
const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showNotification } = useNotification();
  const { selectedTenant } = useTenant();
  const [botList, setBotList] = useState<BotListResponse | null>(null);
  const [initialLoading, setInitialLoading] = useState(true); // Only true on first page load
  const [refreshing, setRefreshing] = useState(false); // True when updating data (filters, etc.)
  const [error, setError] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState<number | 'custom'>(5);
  const [customStart, setCustomStart] = useState<string>(() => dayjs().subtract(5, 'minute').format('YYYY-MM-DDTHH:mm'));
  const [customEnd, setCustomEnd] = useState<string>(() => dayjs().format('YYYY-MM-DDTHH:mm'));
  const [lastLiveRangeMinutes, setLastLiveRangeMinutes] = useState(5);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [backgroundBotList, setBackgroundBotList] = useState<BotListResponse | null>(null);
  const lastSeenRunningRef = useRef<number | null>(null);
  const [hasAnimated, setHasAnimated] = useState(false); // Track if initial animation has played
  const [currentScenario, setCurrentScenario] = useState<Scenario | null>(null);
  // Every active scenario id sharing the current scenario's name — bots can be split across
  // same-name scenarios, so metrics match all of them (scenario_id=~) to keep every bot visible.
  const [currentScenarioIds, setCurrentScenarioIds] = useState<string[] | undefined>(undefined);
  const [filterProvider, setFilterProvider] = useState<string | null>(null);
  const [filterRegion, setFilterRegion] = useState<string | null>(null);
  const [filterPublicIp, setFilterPublicIp] = useState<string | null>(null);
  // Multi-select of statuses to HIDE from the bot table. Defaults to hiding
  // "error" bots so operators start with a clean fleet view; clear the chip to
  // bring them back.
  const [hiddenStatuses, setHiddenStatuses] = useState<string[]>(['error']);
  const [panelOrder, setPanelOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(PANEL_ORDER_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) return parsed;
    } catch {}
    return [
      'botsTable',
      'bandwidth',
      'packets',
      'botsByProvider',
      'tcp',
      'rps',
      'successRate',
      'responseTime',
    ];
  });
  const [pinnedPanelIds, setPinnedPanelIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(PINNED_PANELS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) return parsed;
    } catch {}
    return [];
  });
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const botPollIntervalMs = useAdaptivePolling({ activeMs: 5000, idleMs: 30000 });

  // Fetch data when selectedRange, custom range, or tenant changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Only show full loading state on initial page load
        if (initialLoading) {
          setInitialLoading(true);
        } else {
          setRefreshing(true); // Just show a subtle indicator for filter changes
        }
        
        const botListData = await api.listBots();
        setBotList(botListData);
        lastSeenRunningRef.current = botListData?.total_running ?? null;
        setError(null);
        
        // Mark as animated after first successful load
        if (!hasAnimated) {
          setTimeout(() => setHasAnimated(true), 1500); // After animations complete
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data');
      } finally {
        setInitialLoading(false);
        setRefreshing(false);
      }
    };
    fetchData();
  }, [selectedRange, customStart, customEnd, selectedTenant, hasAnimated, initialLoading]);

  useEffect(() => {
    let isMounted = true;
    const fetchScenario = async () => {
      try {
        const data = await api.getCurrentScenario();
        if (isMounted) {
          setCurrentScenario(data?.scenario ?? null);
          setCurrentScenarioIds(data?.scenario_ids ?? undefined);
        }
      } catch {
        if (isMounted) {
          setCurrentScenario(null);
          setCurrentScenarioIds(undefined);
        }
      }
    };
    fetchScenario();
    return () => {
      isMounted = false;
    };
  }, [selectedTenant]);

  // Background polling
  useEffect(() => {
    let interval: NodeJS.Timeout;
    const poll = async () => {
      try {
        setBackgroundLoading(true);
        const botListData = await api.listBots();
        
        if (botListData) {
          const newRunning = botListData.total_running;
          const prevRunning = lastSeenRunningRef.current;

          if (prevRunning !== null) {
            if (newRunning > prevRunning) {
              showNotification(`${newRunning - prevRunning} more bots are now running`, 'success');
            } else if (newRunning < prevRunning) {
              showNotification(`${prevRunning - newRunning} bots have stopped running`, 'warning');
            }
          }

          lastSeenRunningRef.current = newRunning;
        }
        
        setBackgroundBotList(botListData);
      } catch (e) {
        // ignore background errors
      } finally {
        setBackgroundLoading(false);
      }
    };
    interval = setInterval(poll, botPollIntervalMs);
    return () => clearInterval(interval);
  }, [botPollIntervalMs, showNotification, selectedTenant]);

  const handleApplyBackgroundData = () => {
    if (backgroundBotList) setBotList(backgroundBotList);
    showNotification('Dashboard data has been updated with latest information', 'info');
  };

  const handleRangeChange = (_: any, newValue: number | 'custom' | null) => {
    if (newValue !== null) setSelectedRange(newValue);
  };

  const handleShiftRange = (direction: -1 | 1) => {
    if (selectedRange === 'custom') {
      const start = dayjs(customStart);
      const end = dayjs(customEnd);
      if (!start.isValid() || !end.isValid()) return;
      const diffMs = end.diff(start);
      if (diffMs <= 0) return;
      const newStart = start.add(diffMs * direction, 'millisecond');
      const newEnd = end.add(diffMs * direction, 'millisecond');
      setCustomStart(newStart.format('YYYY-MM-DDTHH:mm'));
      setCustomEnd(newEnd.format('YYYY-MM-DDTHH:mm'));
      return;
    }

    const rangeMinutes = selectedRange;
    const end = dayjs().subtract(rangeMinutes, 'minute');
    const start = end.subtract(rangeMinutes, 'minute');
    setCustomStart(start.format('YYYY-MM-DDTHH:mm'));
    setCustomEnd(end.format('YYYY-MM-DDTHH:mm'));
    setSelectedRange('custom');
  };

  // ---- Scenario telemetry hooks MUST be called before any early returns ----
  // useMemo is critical here: an IIFE would create a new object reference on every render,
  // causing fetchMetrics to be recreated → useEffect fires → fetch aborted → Network Error loop.
  const telemetryRangeOverride = React.useMemo(() => {
    if (selectedRange !== 'custom') return null;
    const start = dayjs(customStart);
    const end = dayjs(customEnd);
    if (!start.isValid() || !end.isValid()) return null;
    const startSec = start.unix();
    const endSec = end.unix();
    if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) return null;
    return { startSec, endSec };
  }, [selectedRange, customStart, customEnd]);

  const telemetryRangeSeconds = React.useMemo(() => {
    if (selectedRange === 'custom') {
      const start = dayjs(customStart);
      const end = dayjs(customEnd);
      const diff = end.diff(start, 'second');
      return diff > 0 ? diff : 300;
    }
    return selectedRange * 60;
  }, [selectedRange, customStart, customEnd]);

  const { metrics: telemetry, error: telemetryError } = useGrafanaMetrics({
    tenantId: selectedTenant?.tenant_id,
    scenarioId: currentScenario?.id,
    scenarioIds: currentScenarioIds,
    refreshInterval: 5000,
    timeRange: telemetryRangeSeconds,
    range: telemetryRangeOverride,
    filters: {
      provider: filterProvider,
      region: filterRegion,
      publicIp: filterPublicIp,
    },
  });

  // Bot table rows come from the database (ground-truth inventory), scoped to
  // the active scenario. The heartbeat overlay (reporting/last-seen) is derived
  // inside the hook from fresh Prometheus telemetry matched by public_ip.
  const { rows: dbBots } = useDashboardBots({
    tenantId: selectedTenant?.tenant_id,
    scenarioId: currentScenario?.id,
    scenarioIds: currentScenarioIds,
    refreshInterval: 5000,
  });

  const { metrics: botsChartMetrics } = useGrafanaMetrics({
    tenantId: selectedTenant?.tenant_id,
    scenarioId: currentScenario?.id,
    scenarioIds: currentScenarioIds,
    refreshInterval: 5000,
    timeRange: telemetryRangeSeconds,
    range: telemetryRangeOverride,
    disableLiveBuffer: true,
    filters: {
      provider: filterProvider,
      region: filterRegion,
      publicIp: filterPublicIp,
    },
  });

  useGrafanaMetrics({
    tenantId: selectedTenant?.tenant_id,
    scenarioId: currentScenario?.id,
    scenarioIds: currentScenarioIds,
    refreshInterval: 5000,
    timeRange: lastLiveRangeMinutes * 60,
    filters: {
      provider: filterProvider,
      region: filterRegion,
      publicIp: filterPublicIp,
    },
    enabled: selectedRange === 'custom',
  });

  const attackTargets = React.useMemo(() => {
    const raw = (currentScenario?.configuration as any)?.targets;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(Boolean)
      .map((target) => String(target).trim())
      .filter(Boolean);
  }, [currentScenario]);

  const formatSi = (raw: number, unit: string, absolute = false) => {
    if (!Number.isFinite(raw)) return '—';
    const value = Math.abs(raw);
    const sign = absolute ? '' : raw < 0 ? '-' : '';
    const thresholds: Array<[number, string]> = [
      [1e12, 'T'],
      [1e9, 'G'],
      [1e6, 'M'],
      [1e3, 'K'],
    ];
    for (const [t, prefix] of thresholds) {
      if (value >= t) return `${sign}${(value / t).toFixed(2)} ${prefix}${unit}`;
    }
    return `${sign}${value.toFixed(2)} ${unit}`;
  };

  const formatPinnedValue = (
    value: number | null,
    formatter?: (value: number) => string,
    unit?: string
  ) => {
    if (value === null || !Number.isFinite(value)) return '—';
    const formatted = formatter ? formatter(value) : value.toLocaleString();
    const asText = typeof formatted === 'string' ? formatted : String(formatted);
    const parts = asText.trim().split(/\s+/);
    const hasUnitToken = parts.length > 1 && /[a-zA-Z]/.test(parts[parts.length - 1]);
    const inferredUnit = hasUnitToken ? parts[parts.length - 1] : undefined;
    const numericText = hasUnitToken ? parts.slice(0, -1).join(' ') : asText;
    const unitText = unit ?? inferredUnit;
    if (!unitText) return numericText;
    return (
      <>
        {numericText}
        <Box component="span" sx={{ fontSize: '0.3em', opacity: 0.65, ml: 0.1 }}>
          {unitText}
        </Box>
      </>
    );
  };

  const formatMaybeDecimal = (value: number, digits = 2) => {
    if (!Number.isFinite(value)) return '0';
    const abs = Math.abs(value);
    if (abs > 0 && abs < 1) {
      return value.toFixed(digits);
    }
    return value.toFixed(0);
  };

  const telemetryDomain: [number, number] = telemetryRangeOverride
    ? [telemetryRangeOverride.startSec * 1000, telemetryRangeOverride.endSec * 1000]
    : [Date.now() - telemetryRangeSeconds * 1000, Date.now()];

  const providerSeries = React.useMemo(() => {
    const palette = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.error.main,
      theme.palette.primary.light,
    ];
    const entries = Object.entries(botsChartMetrics.botsByProvider || {}).sort(([a], [b]) => a.localeCompare(b));
    const hasKnown = entries.some(([provider]) => provider.toLowerCase() !== 'unknown');
    const filtered = hasKnown ? entries.filter(([provider]) => provider.toLowerCase() !== 'unknown') : entries;
    if (filtered.length === 0) {
      return [
        {
          id: 'bots_total',
          label: 'Bots',
          color: alpha(theme.palette.primary.main, 0.75),
          data: [],
          type: 'line' as const,
          strokeWidth: 3,
        },
      ];
    }
    return filtered.map(([provider, data], idx) => ({
      id: provider.replace(/[^a-zA-Z0-9_]/g, '_'),
      label: provider,
      color: palette[idx % palette.length],
      data,
    }));
  }, [
    botsChartMetrics.botsByProvider,
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
    theme.palette.primary.light,
  ]);

  const botsByProviderYDomain = React.useMemo((): [number, number] => {
    const end = telemetryRangeOverride ? telemetryRangeOverride.endSec * 1000 : Date.now();
    const start = telemetryRangeOverride ? telemetryRangeOverride.startSec * 1000 : end - telemetryRangeSeconds * 1000;
    // The chart stacks providers, so each column reaches the SUM of the series at
    // that timestamp, not the max of a single series. Domain the axis on the
    // per-timestamp stacked total so the topmost line isn't clipped.
    const totalByTs = new Map<number, number>();
    for (const s of providerSeries) {
      for (const p of s.data) {
        if (p.timestamp >= start && p.timestamp <= end) {
          totalByTs.set(p.timestamp, (totalByTs.get(p.timestamp) ?? 0) + p.value);
        }
      }
    }
    const peak = Array.from(totalByTs.values()).reduce((max, total) => Math.max(max, total), 0);
    // Bots are small integer counts. Letting recharts auto-domain inflates the axis
    // to fit its default integer ticks, so a single bot renders as a tiny step glued
    // to the bottom. Pin the top to the actual stacked peak (min 1, so a flat line at
    // 0 stays visible) to keep the axis tight to the data.
    return [0, Math.max(1, Math.ceil(peak))];
  }, [providerSeries, telemetryRangeSeconds, telemetryRangeOverride]);

  const botsByProviderYAxisPadding = React.useMemo(
    // Lift the top and bottom lines off the chart edges so neither is clipped.
    () => ({ top: 8, bottom: 8 } as const),
    []
  );

  // Apply the dashboard's provider/region/IP filters to the DB-sourced rows.
  const botsRows = React.useMemo(
    () =>
      dbBots.filter(
        (r) =>
          (!filterProvider || r.provider === filterProvider) &&
          (!filterRegion || r.region === filterRegion) &&
          (!filterPublicIp || r.public_ip === filterPublicIp) &&
          !hiddenStatuses.includes((r.status || '').toLowerCase())
      ),
    [dbBots, filterProvider, filterRegion, filterPublicIp, hiddenStatuses]
  );

  useEffect(() => {
    if (typeof selectedRange === 'number') {
      setLastLiveRangeMinutes(selectedRange);
    }
  }, [selectedRange]);

  const getTimeseriesConfig = (panelId: string) => {
    switch (panelId) {
      case 'bandwidth':
        return {
          title: 'Bandwidth in/out',
          icon: NetworkIcon,
          height: 260,
          series: [
            { id: 'down', label: 'In', color: theme.palette.primary.main, data: telemetry.bandwidthDown, type: 'area' as const },
            { id: 'up', label: 'Out', color: theme.palette.secondary.main, data: telemetry.bandwidthUp, type: 'area' as const },
          ],
          yTickFormatter: (v: number) => formatSi(v, 'bps', true),
          valueFormatter: (v: number) => formatSi(v, 'bps', true),
        };
      case 'packets':
        return {
          title: 'Packets per second',
          icon: SpeedIcon,
          height: 260,
          series: [
            { id: 'up', label: 'Up', color: theme.palette.success.main, data: telemetry.packetsUp },
            { id: 'down', label: 'Down', color: theme.palette.warning.main, data: telemetry.packetsDown },
          ],
          yTickFormatter: (v: number) => formatSi(v, 'pps', true),
          valueFormatter: (v: number) => formatSi(v, 'pps', true),
        };
      case 'botsByProvider':
        return {
          title: 'Bot by Providers',
          icon: ComputerIcon,
          height: 260,
          series: providerSeries,
          yTickFormatter: (v: number) => `${Math.round(v)}`,
          valueFormatter: (v: number) => `${Math.round(v)}`,
          yAxisAllowDecimals: false,
          yDomain: botsByProviderYDomain,
          yAxisPadding: botsByProviderYAxisPadding,
          stacked: true,
          showTotal: true,
        };
      case 'tcp':
        return {
          title: 'Concurrent TCP Connections',
          icon: NetworkIcon,
          height: 260,
          series: [
            { id: 'connections', label: 'Connections', color: theme.palette.info.main, data: telemetry.tcpConnections },
            { id: 'closed', label: 'Closed', color: theme.palette.error.main, data: telemetry.tcpConnectionsClosed },
          ],
          yTickFormatter: (v: number) => `${Math.round(v)}`,
          valueFormatter: (v: number) => `${Math.round(v)}`,
          yAxisAllowDecimals: false,
        };
      case 'rps':
        return {
          // Socket attacks (TCP connection flood, DNS query flood, ...) report
          // completed operations through the same counters -- call them that.
          title: isSocketOperationAttackClass(currentScenario?.attack_type?.attack_class)
            ? 'Operations per Second'
            : 'Requests per Second',
          icon: SpeedIcon,
          height: 260,
          series: [
            { id: 'ok', label: 'OK', color: theme.palette.success.main, data: telemetry.rpsOk },
            { id: 'error', label: 'ERROR', color: theme.palette.error.main, data: telemetry.rpsError },
            { id: 'total', label: 'TOTAL', color: theme.palette.primary.main, data: telemetry.rpsTotal },
          ],
          yTickFormatter: (v: number) => formatMaybeDecimal(v),
          valueFormatter: (v: number) => formatMaybeDecimal(v),
        };
      case 'successRate':
        return {
          title: 'Success rate',
          icon: SpeedIcon,
          height: 260,
          unit: '%',
          series: [{ id: 'success', label: 'Success', color: theme.palette.success.main, data: telemetry.successRate, type: 'area' as const }],
          yTickFormatter: (v: number) => `${v.toFixed(0)}%`,
          valueFormatter: (v: number) => `${v.toFixed(1)}`,
          yDomain: [0, 100] as [number, number],
        };
      case 'responseTime':
        return {
          title: 'Response time',
          icon: SpeedIcon,
          height: 260,
          unit: 's',
          series: [{ id: 'rt', label: 'Avg', color: theme.palette.info.main, data: telemetry.responseTime, type: 'area' as const }],
          yTickFormatter: (v: number) => `${v.toFixed(2)}s`,
          valueFormatter: (v: number) => v.toFixed(3),
        };
      default:
        return null;
    }
  };

  const getPinnedValues = (config: ReturnType<typeof getTimeseriesConfig>) => {
    if (!config) return [];
    const values = config.series.map((s) => ({
      label: s.label,
      color: s.color,
      value: s.data.length > 0 ? s.data[s.data.length - 1].value : 0,
    }));
    if (values.length > 3) {
      return values
        .slice()
        .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity))
        .slice(0, 3);
    }
    return values;
  };

  const handleTogglePin = (panelId: string) => {
    setPinnedPanelIds((prev) =>
      prev.includes(panelId) ? prev.filter((id) => id !== panelId) : [...prev, panelId]
    );
  };

  const pinnedConfigs = pinnedPanelIds
    .map((id) => ({ id, config: getTimeseriesConfig(id) }))
    .filter((entry): entry is { id: string; config: NonNullable<ReturnType<typeof getTimeseriesConfig>> } => !!entry.config);

  useEffect(() => {
    try {
      localStorage.setItem(PANEL_ORDER_STORAGE_KEY, JSON.stringify(panelOrder));
    } catch {}
  }, [panelOrder]);

  const allowedPinnedPanels = React.useMemo(
    () => new Set(panelOrder.filter((id) => id !== 'botsTable')),
    [panelOrder]
  );

  useEffect(() => {
    setPinnedPanelIds((prev) => {
      const next = prev.filter((id) => allowedPinnedPanels.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [allowedPinnedPanels]);

  useEffect(() => {
    try {
      localStorage.setItem(PINNED_PANELS_STORAGE_KEY, JSON.stringify(pinnedPanelIds));
    } catch {}
  }, [pinnedPanelIds]);

  // Only show skeleton on initial page load, not on filter changes
  if (initialLoading) {
    return (
      <Box>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            color: 'text.primary',
          }}
        >
          Dashboard
        </Typography>
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Skeleton 
                variant="rectangular" 
                height={200}
                sx={{
                  borderRadius: 4,
                  backgroundColor: 'action.disabledBackground',
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            color: 'text.primary',
          }}
        >
          Dashboard
        </Typography>
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            animation: `${fadeSlideUp} 0.3s ease-out`,
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!botList) {
    return null;
  }

  const backgroundStats =
    !!backgroundBotList &&
    (backgroundBotList.total_running !== botList.total_running ||
      backgroundBotList.total_shutdown !== botList.total_shutdown ||
      backgroundBotList.total_unrelated !== botList.total_unrelated);

  return (
    <PermissionGate permission="view_dashboard" fallback={
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" color="text.secondary" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You need viewer or higher permissions to access the Dashboard.
        </Typography>
      </Box>
    }>
      <Box>
        {/* Header */}
        <Box 
          sx={{ 
            mb: 4,
            // Only animate on first load
            ...(hasAnimated ? {
              opacity: 1,
            } : {
              animation: `${fadeSlideUp} 0.5s ease-out`,
            }),
          }}
        >
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 'bold', 
              fontFamily: '"JetBrains Mono", monospace',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.background.default} 50%, ${theme.palette.primary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: `0 0 30px ${alpha(theme.palette.primary.main, isDark ? 0.3 : 0.2)}`,
              letterSpacing: '-0.02em',
              mb: 1,
            }}
          >
            DASHBOARD
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary',
              fontFamily: '"Space Mono", monospace',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            Real-time Operations Dashboard
          </Typography>
          {attackTargets.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0,
                minHeight: 30,
                maxWidth: '100%',
                borderRadius: '8px',
                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.28)}`,
                backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.45 : 0.7),
                overflow: 'hidden',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography
                sx={{
                  px: 1.25,
                  py: 0.5,
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Targets
              </Typography>
              <Box
                sx={{
                  alignSelf: 'stretch',
                  width: '1px',
                  backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.22),
                }}
              />
              <Typography
                sx={{
                  px: 1.25,
                  py: 0.5,
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.8rem',
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: { xs: 'calc(100vw - 80px)', sm: 520, md: 720 },
                }}
                title={attackTargets.join(', ')}
              >
                {(() => {
                  const maxInlineTargets = 3;
                  const hasMore = attackTargets.length > maxInlineTargets;
                  const visibleTargets = hasMore ? attackTargets.slice(0, maxInlineTargets) : attackTargets;
                  const remainingCount = attackTargets.length - maxInlineTargets;
                  return `${visibleTargets.join(', ')}${hasMore ? ` ... +${remainingCount} more` : ''}`;
                })()}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Time Range Controls */}
        <Box 
          sx={{ 
            mb: 4, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            flexWrap: 'wrap',
            // Only animate on first load
            ...(hasAnimated ? {
              opacity: 1,
            } : {
              animation: `${fadeSlideUp} 0.5s ease-out`,
              animationDelay: '0.1s',
              opacity: 0,
              animationFillMode: 'forwards',
            }),
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleShiftRange(-1)}
            sx={{
              minWidth: 32,
              px: 1,
              fontFamily: '"Space Mono", monospace',
              borderColor: alpha(theme.palette.primary.main, 0.35),
              color: theme.palette.primary.main,
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </Button>
          <ToggleButtonGroup
            value={selectedRange}
            exclusive
            onChange={handleRangeChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
              },
            }}
          >
            {TIME_PRESETS.map(preset => (
              <ToggleButton key={preset.value} value={preset.value}>
                {preset.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleShiftRange(1)}
            disabled={selectedRange !== 'custom'}
            sx={{
              minWidth: 32,
              px: 1,
              fontFamily: '"Space Mono", monospace',
              borderColor: alpha(theme.palette.primary.main, 0.35),
              color: theme.palette.primary.main,
              opacity: selectedRange !== 'custom' ? 0.4 : 1,
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </Button>
          {selectedRange === 'custom' && (
            <>
              <TextField
                label="Start"
                type="datetime-local"
                value={customStart}
                onChange={e => setCustomStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="End"
                type="datetime-local"
                value={customEnd}
                onChange={e => setCustomEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </>
          )}
          {(backgroundLoading || refreshing) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  animation: `${pulse} 1s ease-in-out infinite`,
                  boxShadow: `0 0 10px ${alpha(theme.palette.primary.main, 0.8)}`,
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.75rem',
                }}
              >
                {refreshing ? 'Updating...' : 'Syncing...'}
              </Typography>
            </Box>
          )}
          {(backgroundStats && backgroundBotList) && (
            <Button 
              size="small" 
              variant="outlined" 
              onClick={handleApplyBackgroundData}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
              }}
            >
              Update to Latest
            </Button>
          )}
        </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {pinnedConfigs.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {pinnedConfigs.map(({ id, config }, index) => {
            const values = getPinnedValues(config);
            const Icon = config.icon;
            const count = pinnedConfigs.length;
            const remainder = count % 3;
            const isLastRowSingle = remainder === 1 && index === count - 1;
            const isLastRowPair = remainder === 2 && index >= count - 2;
            const mdSize = count === 1 ? 12 : count === 2 ? 6 : isLastRowSingle ? 12 : 6;
            const lgSize = count === 1 ? 12 : count === 2 ? 6 : isLastRowSingle ? 12 : isLastRowPair ? 6 : 4;
            return (
              <Grid
                item
                xs={12}
                md={mdSize}
                lg={lgSize}
                key={id}
              >
                <Card
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.7 : 0.95),
                    border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.2)}`,
                    borderRadius: 2,
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, isDark ? 0.08 : 0.12)}`,
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {Icon && (
                          <Box
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.18),
                              borderRadius: '10px',
                              p: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.3)}`,
                            }}
                          >
                            <Icon sx={{ color: theme.palette.primary.main, fontSize: '1.2rem' }} />
                          </Box>
                        )}
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '1.2rem',
                              color: 'text.primary',
                              fontWeight: 700,
                            }}
                          >
                            {config.title}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${Math.max(values.length, 1)}, minmax(0, 1fr))`,
                        gap: 3,
                        width: '100%',
                        alignItems: 'center',
                        textAlign: 'center',
                      }}
                    >
                      {values.map((v) => (
                        <Box
                          key={v.label}
                          sx={{
                            minWidth: 140,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: '"Space Mono", monospace',
                              fontSize: '0.85rem',
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              letterSpacing: '0.08em',
                              mb: 0.5,
                            }}
                          >
                            {v.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: { xs: '2rem', md: '2.6rem' },
                              fontWeight: 800,
                              color: v.color,
                              textShadow: `0 0 18px ${alpha(v.color, isDark ? 0.25 : 0.18)}`,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {formatPinnedValue(v.value, config.valueFormatter, config.unit)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* Grafana-style Scenario Telemetry */}
        <Grid item xs={12}>
          {!selectedTenant ? (
            <Alert severity="info">Select a tenant to view telemetry.</Alert>
          ) : !currentScenario ? (
            <Alert severity="info">No active scenario. Start a scenario to view Grafana panels.</Alert>
          ) : telemetryError ? (
            <Alert severity="error">{telemetryError}</Alert>
          ) : (
            <Box>
              {/* Filters (Grafana-like) */}
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'center',
                }}
              >
                <Autocomplete
                  size="small"
                  value={filterProvider}
                  onChange={(_, v) => setFilterProvider(v)}
                  options={Array.from(new Set(dbBots.map((r) => r.provider))).sort()}
                  renderInput={(params) => (
                    <TextField {...params} label="Provider" placeholder="All" />
                  )}
                  sx={{ minWidth: 220 }}
                />
                <Autocomplete
                  size="small"
                  value={filterRegion}
                  onChange={(_, v) => setFilterRegion(v)}
                  options={Array.from(new Set(dbBots.map((r) => r.region))).sort()}
                  getOptionLabel={formatRegionLocation}
                  renderOption={(props, code) => <li {...props}><RegionLocation region={code} compact /></li>}
                  renderInput={(params) => (
                    <TextField {...params} label="Region" placeholder="All" />
                  )}
                  sx={{ minWidth: 220 }}
                />
                <Autocomplete
                  size="small"
                  value={filterPublicIp}
                  onChange={(_, v) => setFilterPublicIp(v)}
                  freeSolo
                  options={Array.from(new Set(dbBots.map((r) => r.public_ip))).filter(Boolean).sort()}
                  renderInput={(params) => (
                    <TextField {...params} label="Public IP" placeholder="All" />
                  )}
                  sx={{ minWidth: 260 }}
                />
                <Autocomplete
                  multiple
                  size="small"
                  value={hiddenStatuses}
                  onChange={(_, v) => setHiddenStatuses(v.map((s) => s.toLowerCase()))}
                  options={Array.from(
                    new Set([
                      ...dbBots.map((r) => (r.status || '').toLowerCase()).filter(Boolean),
                      ...hiddenStatuses,
                    ])
                  ).sort()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Hide status"
                      placeholder={hiddenStatuses.length === 0 ? 'None' : ''}
                    />
                  )}
                  sx={{ minWidth: 260 }}
                />

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setFilterProvider(null);
                    setFilterRegion(null);
                    setFilterPublicIp(null);
                    setHiddenStatuses(['error']);
                  }}
                  sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.75rem' }}
                >
                  Clear Filters
                </Button>
              </Box>

              <Grid container spacing={2}>
                {panelOrder.map((panelId) => {
                  const renderPanel = () => {
                    if (panelId === 'botsTable') {
                      return (
                        <BotsIpRegionTable rows={botsRows} />
                      );
                    }
                    const config = getTimeseriesConfig(panelId);
                    if (!config) return null;
                    const panelDomain = telemetryDomain;
                    return (
                      <TimeseriesCard
                        title={config.title}
                        icon={config.icon}
                        height={config.height}
                        unit={config.unit}
                        xDomain={panelDomain}
                        series={config.series}
                        yTickFormatter={config.yTickFormatter}
                        valueFormatter={config.valueFormatter}
                        yAxisAllowDecimals={config.yAxisAllowDecimals}
                        yDomain={config.yDomain}
                        yAxisPadding={'yAxisPadding' in config ? (config as any).yAxisPadding : undefined}
                        stacked={'stacked' in config ? (config as any).stacked : undefined}
                        showTotal={'showTotal' in config ? (config as any).showTotal : undefined}
                        isPinned={pinnedPanelIds.includes(panelId)}
                        onTogglePin={() => handleTogglePin(panelId)}
                      />
                    );
                  };

                  const gridProps =
                    panelId === 'botsTable' ? { xs: 12 } : { xs: 12, md: 6 };

                  return (
                    <Grid
                      item
                      key={panelId}
                      {...gridProps}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOverId(panelId);
                      }}
                      onDragLeave={() => setDragOverId((prev) => (prev === panelId ? null : prev))}
                      onDrop={(e) => {
                        e.preventDefault();
                        const source = e.dataTransfer.getData('text/plain');
                        if (!source || source === panelId) return;
                        setPanelOrder((prev) => {
                          const next = prev.filter((x) => x !== source);
                          const idx = next.indexOf(panelId);
                          next.splice(idx >= 0 ? idx : next.length, 0, source);
                          return next;
                        });
                        setDragOverId(null);
                        setDraggingId(null);
                      }}
                      sx={{
                        position: 'relative',
                        outline:
                          dragOverId === panelId
                            ? `2px dashed ${alpha(theme.palette.primary.main, 0.6)}`
                            : 'none',
                        outlineOffset: '6px',
                        borderRadius: 2,
                      }}
                    >
                      {/* Drag handle (3-dots style) */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          zIndex: 5,
                          opacity: 0,
                          transition: 'opacity 0.2s ease',
                          '&:hover': { opacity: 1 },
                        }}
                      >
                        <Box
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', panelId);
                            setDraggingId(panelId);
                            setDragOverId(null);
                          }}
                          onDragEnd={() => {
                            setDraggingId(null);
                            setDragOverId(null);
                          }}
                          sx={{
                            cursor: draggingId === panelId ? 'grabbing' : 'grab',
                            width: 34,
                            height: 34,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.65 : 0.9),
                            border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.2)}`,
                            backdropFilter: 'blur(10px)',
                          }}
                          title="Drag to reorder"
                        >
                          <DragIndicatorIcon sx={{ color: theme.palette.primary.main, fontSize: '1.1rem' }} />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          '&:hover > .drag-handle': { opacity: 1 },
                        }}
                      >
                        {renderPanel()}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Availability monitoring panel -- below all graphs */}
              <Box sx={{ mt: 3 }}>
                <MonitoringPanel
                  tenantId={selectedTenant!.tenant_id}
                  scenarioId={currentScenario!.id}
                />
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
    </PermissionGate>
  );
};

export default Dashboard;
